import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Check, Lock, Clock, ArrowLeft, Zap, Crown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Countdown } from '../components/app/Countdown';
import { ENABLE_DAY4 } from '../utils/helpers';
import { lessonsIndex } from '../data/lessonsIndex';

// Calculate unlock status based on user start date
const calculateDayStatus = (dayNum, user, now) => {
  // ADMIN BYPASS: gabka2000@promptorigin.pl has access to ALL days
  if (user?.email?.toLowerCase() === 'gabka2000@promptorigin.pl') {
    return { accessible: true, reason: 'admin_bypass' };
  }

  // Day 1 always accessible
  if (dayNum === 1) {
    return { accessible: true, reason: 'available' };
  }

  // Get user start date
  const startDate = user?.created_at ? new Date(user.created_at) : null;
  if (!startDate) {
    return { accessible: false, reason: 'no_start_date' };
  }

  // Calculate days elapsed since start + bonus days
  const msElapsed = now.getTime() - startDate.getTime();
  const daysElapsed = Math.floor(msElapsed / (24 * 60 * 60 * 1000));
  const bonusDays = user?.bonus_days || 0;

  // Max available day = 1 + full days elapsed + bonus days from accelerator
  const maxAvailableDay = 1 + daysElapsed + bonusDays;

  // Paywall check for day > 3
  if (dayNum > 3 && user?.subscription_status !== 'active' && !ENABLE_DAY4) {
    return { accessible: false, reason: 'paywall' };
  }

  // Time-based unlock
  if (dayNum <= maxAvailableDay) {
    return { accessible: true, reason: 'available' };
  }

  // Calculate when this day unlocks
  const unlockTime = new Date(startDate.getTime() + (dayNum - 1) * 24 * 60 * 60 * 1000);
  const remainingMs = unlockTime.getTime() - now.getTime();
  const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

  return {
    accessible: false,
    reason: 'time_locked',
    remaining_seconds: remainingSeconds,
    unlock_time: unlockTime.toISOString()
  };
};

export const LessonsPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [accStatus, setAccStatus] = useState({
    remaining: 2,
    uses_this_month: 0,
    is_premium: false,
    bonus_days: 0,
  });
  const [accLoading, setAccLoading] = useState(false);

  // Update "now" every minute to recalculate statuses + fetch accelerator status
  useEffect(() => {
    setLoading(false);
    const interval = setInterval(() => setNow(new Date()), 60000);

    api
      .get('/lessons/accelerator-status')
      .then((r) => setAccStatus(r.data))
      .catch(() => {});

    return () => clearInterval(interval);
  }, [api]);

  // Calculate day statuses
  const dayStatuses = useMemo(() => {
    const statuses = {};
    lessonsIndex.forEach((lesson, idx) => {
      const dayNum = lesson.day_number ?? (idx + 1);
      statuses[dayNum] = calculateDayStatus(dayNum, user, now);
    });
    return statuses;
  }, [user, now]);

  // Find first locked day for countdown
  const firstLockedDay = useMemo(() => {
    for (let i = 0; i < lessonsIndex.length; i++) {
      const dayNum = lessonsIndex[i].day_number ?? (i + 1);
      const status = dayStatuses[dayNum];
      if (status?.reason === 'time_locked' && status.remaining_seconds > 0) {
        return dayNum;
      }
    }
    return null;
  }, [dayStatuses]);

  // Find first locked day (any reason) for showing Accelerator card to all users
  const hasAnyLockedDay = useMemo(() => {
    for (let i = 0; i < lessonsIndex.length; i++) {
      const dayNum = lessonsIndex[i].day_number ?? (i + 1);
      const status = dayStatuses[dayNum];
      if (status && !status.accessible) {
        return true;
      }
    }
    return false;
  }, [dayStatuses]);

  const completedDays = user?.completed_days || [];
  const isCompleted = (dayNum) => completedDays.includes(dayNum);

  // Refresh page when countdown completes
  const handleCountdownComplete = () => {
    setNow(new Date());
  };

  const handleAccelerate = async () => {
    if (user?.subscription_status !== 'active') {
      navigate('/paywall');
      window.scrollTo(0, 0);
      return;
    }

    if (accLoading) return;
    setAccLoading(true);

    try {
      const res = await api.post('/lessons/accelerate');

      setAccStatus((prev) => ({
        ...prev,
        remaining: res.data.remaining,
        uses_this_month: res.data.uses_this_month,
        bonus_days: res.data.bonus_days,
      }));

      await refreshUser();
      setNow(new Date());
      toast.success('Kolejny dzień odblokowany!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się przyspieszyć dnia');
    } finally {
      setAccLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 140px)',
        background: 'transparent',
      }}
      data-testid="lessons-page"
    >
      <style>{`
        .day-card-locked {
          opacity: 0.55;
          pointer-events: none;
        }
        .day-card-locked:hover {
          transform: none !important;
          box-shadow: none !important;
        }
        .day-card-available {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .day-card-available:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(56, 189, 248, 0.15);
        }
        .unlock-message {
          font-size: 0.7rem;
          color: rgba(148, 163, 184, 0.7);
          margin-top: 2px;
        }
      `}</style>

      <div className="px-6 pt-8 pb-6">
        <button
          onClick={() => navigate('/today')}
          className="mb-4 text-slate-400 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-white mb-2">Wszystkie lekcje</h1>
        <p className="text-slate-400">30 dni do mistrzostwa promptów</p>
      </div>

      <div className="px-6 space-y-3">
        {/* Day Accelerator */}
        {hasAnyLockedDay && (
          <div
            className="rounded-2xl p-4 mb-2"
            style={{
              background: accStatus.is_premium
                ? 'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(56,189,248,0.04))'
                : 'rgba(255,255,255,0.03)',
              border: accStatus.is_premium
                ? '1px solid rgba(14,165,233,0.15)'
                : '1px solid rgba(255,255,255,0.06)',
            }}
            data-testid="day-accelerator"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: accStatus.is_premium
                      ? 'linear-gradient(135deg, #0ea5e9, #38bdf8)'
                      : 'rgba(100,116,139,0.2)',
                    boxShadow: accStatus.is_premium ? '0 3px 12px rgba(14,165,233,0.25)' : 'none',
                  }}
                >
                  <Zap className={`w-4 h-4 ${accStatus.is_premium ? 'text-white' : 'text-slate-500'}`} />
                </div>

                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${accStatus.is_premium ? 'text-white' : 'text-slate-400'}`}>
                    Odblokuj kolejny dzień
                  </p>

                  {accStatus.is_premium ? (
                    <p className="text-xs text-slate-500">
                      Pozostało: <span className="text-sky-400 font-medium">{accStatus.remaining}/2</span> w tym miesiącu
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500">Dostępne w planie Premium</p>
                  )}
                </div>
              </div>

              {accStatus.is_premium ? (
                <button
                  onClick={handleAccelerate}
                  disabled={accLoading || accStatus.remaining <= 0}
                  className="flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all active:scale-95"
                  style={{
                    background:
                      accStatus.remaining > 0
                        ? 'linear-gradient(135deg, #0ea5e9, #38bdf8)'
                        : 'rgba(100,116,139,0.2)',
                    opacity: accStatus.remaining > 0 ? 1 : 0.5,
                    boxShadow: accStatus.remaining > 0 ? '0 3px 12px rgba(14,165,233,0.25)' : 'none',
                  }}
                  data-testid="accelerate-btn"
                >
                  {accLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : accStatus.remaining > 0 ? (
                    'Przyspiesz'
                  ) : (
                    'Limit wyczerpany'
                  )}
                </button>
              ) : (
                <button
                  onClick={() => { navigate('/paywall'); window.scrollTo(0, 0); }}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/15 transition-all active:scale-95"
                >
                  <Crown className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-medium text-amber-400">Premium</span>
                </button>
              )}
            </div>
          </div>
        )}

        {lessonsIndex.map((lesson, idx) => {
          const dayNum = lesson.day_number ?? (idx + 1);
          const completed = isCompleted(dayNum);
          const status = dayStatuses[dayNum] || { accessible: false };
          const accessible = status.accessible;
          const isTimeLocked = status.reason === 'time_locked';
          const isPaywalled = status.reason === 'paywall';
          const isFirstLocked = dayNum === firstLockedDay;

          return (
            <Card
              key={dayNum ?? idx}
              className={`animate-slideUp border-0 rounded-2xl ${
                accessible || completed ? 'day-card-available cursor-pointer' : 'day-card-locked'
              }`}
              style={{
                animationDelay: `${idx * 0.05}s`,
                background: accessible || completed ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(8px)',
              }}
              onClick={() => {
                if (accessible || completed) {
                  navigate(`/lesson/${dayNum}`);
                } else if (isPaywalled) {
                  navigate('/paywall');
                }
              }}
            >
              <CardContent className="flex items-center gap-4 py-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    completed
                      ? 'bg-emerald-500'
                      : accessible
                      ? 'bg-sky-500'
                      : isTimeLocked
                      ? 'bg-slate-600'
                      : 'bg-slate-700'
                  }`}
                >
                  {completed ? (
                    <Check className="w-6 h-6 text-white" />
                  ) : accessible ? (
                    <span className="text-white font-bold">{dayNum}</span>
                  ) : isTimeLocked ? (
                    <Clock className="w-5 h-5 text-slate-400" />
                  ) : (
                    <Lock className="w-5 h-5 text-slate-400" />
                  )}
                </div>

                <div className="flex-1">
                  <p className="text-sm text-slate-400">Dzień {dayNum}</p>
                  <p className={`font-semibold ${accessible || completed ? 'text-white' : 'text-slate-400'}`}>
                    {lesson.title ?? `Dzień ${dayNum}`}
                  </p>

                  {isTimeLocked && isFirstLocked && status.remaining_seconds > 0 && (
                    <Countdown seconds={status.remaining_seconds} onComplete={handleCountdownComplete} />
                  )}

                  {isTimeLocked && !isFirstLocked && (
                    <p className="unlock-message">Odblokuje się po ukończeniu poprzednich dni</p>
                  )}

                  {isPaywalled && (
                    <p className="unlock-message">Wymaga subskrypcji Premium</p>
                  )}
                </div>

                {(lesson.is_free ?? dayNum <= 3) && accessible && (
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full font-medium">
                    Darmowe
                  </span>
                )}
              </CardContent>
            </Card>
          );
        })}

        {lessonsIndex.length > 0 && (
          <Card
            className="border-0 mt-6 mb-8 rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <CardContent className="py-6 text-center">
              <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-5 h-5 text-sky-400" />
              </div>
              <p className="text-white font-semibold mb-2">Dzień 31 w drodze</p>
              <p className="text-slate-400 text-sm max-w-xs mx-auto">
                Najpierw przechodzisz pełny program 30 dni. Kolejne dni będziemy dodawać codziennie — pojawią się tu automatycznie.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
